// Brands: the thing a connected account publishes as (ADR-0029).
//
// One person runs `labtest1` and `sabu`, each with a Facebook Page, an Instagram
// account and a YouTube channel. A brand is the drawer those three sit in. It is
// filing and nothing else — it holds no credential, grants no access, and no
// permission check reads it. Which accounts you may reach is still a role
// assignment (ADR-0011), exactly as it was before this existed.
//
// Why not a workspace per brand, given that workspaces already separate things:
// billing and quota are keyed on the workspace, so that shape bills one person
// once per brand and splits their media library the same way. See ADR-0029.

import { supabase } from "./supabase";
import { asAppError } from "./entitlements";

export interface Brand {
  id: string;
  workspace_id: string;
  name: string;
  created_at: string;
}

/** The longest name the column accepts. Checked here so the message is a
 *  sentence rather than a constraint violation arriving from Postgres. */
export const BRAND_NAME_MAX = 80;

export async function listBrands(workspaceId: string): Promise<Brand[]> {
  const { data, error } = await supabase
    .from("brands")
    .select("id, workspace_id, name, created_at")
    .eq("workspace_id", workspaceId)
    .order("name");
  if (error) throw asAppError(error);
  return (data ?? []) as Brand[];
}

/** One field of the form that creates a brand. Blank values are dropped. */
export interface NewBrandDetail {
  key: string;
  label: string;
  value: string;
  sortOrder?: number;
}

/**
 * Create a brand, and fill in what it repeats — business name, phone, the two
 * app store links — in the same breath.
 *
 * Through an RPC because the two halves are one act. Creating the brand and
 * then saving six details in six calls means a brand that can exist with four
 * of them written and no way to say which two the network ate; the function is
 * the transaction. See 20260918000300.
 */
export async function createBrand(
  workspaceId: string, name: string, details: NewBrandDetail[] = [],
): Promise<Brand> {
  const filled = details
    .filter((d) => d.value.trim())
    .map((d) => ({
      key: d.key,
      label: d.label,
      value: d.value.trim(),
      sort_order: d.sortOrder ?? 100,
    }));

  const { data, error } = await supabase.rpc("create_brand_with_details", {
    p_workspace_id: workspaceId,
    p_name: name.trim(),
    p_details: filled,
  });
  if (error) throw asAppError(error);
  return data as Brand;
}

export async function renameBrand(brandId: string, name: string): Promise<Brand> {
  const { data, error } = await supabase
    .from("brands")
    .update({ name: name.trim() })
    .eq("id", brandId)
    .select("id, workspace_id, name, created_at")
    .single();
  if (error) throw asAppError(error);
  return data as Brand;
}

/** Deleting a brand empties a drawer. The accounts in it come back unfiled and
 *  keep publishing — `on delete set null`, never a cascade. */
export async function deleteBrand(brandId: string): Promise<void> {
  const { error } = await supabase.from("brands").delete().eq("id", brandId);
  if (error) throw asAppError(error);
}

/**
 * File an account under a brand, or unfile it with null.
 *
 * Through an RPC rather than an UPDATE, because `social_connections` has no
 * update policy and must not acquire one: a client never writes the table that
 * points at a credential.
 */
export async function setConnectionBrand(
  connectionId: string, brandId: string | null,
): Promise<void> {
  const { error } = await supabase.rpc("set_connection_brand", {
    p_connection_id: connectionId,
    p_brand_id: brandId,
  });
  if (error) throw asAppError(error);
}

// ------------------------------------------------------------- notifications

// Who hears about a brand's posts. Membership grants nothing — it is an
// opt-in mailing list, not access (ADR-0029 §3) — so this is deliberately a
// separate surface from `setConnectionBrand` above rather than reusing its
// permission story beyond "who may organise this brand".

export interface BrandAdmin {
  user_id: string;
  email: string | null;
  role: string;
}

export interface BrandMember {
  user_id: string;
  email: string | null;
  notifications_enabled: boolean;
  joined_at: string;
}

export interface BrandNotificationRoster {
  brand_id: string;
  /** Workspace owners/admins — notified for every brand's posts regardless
   *  of membership below. Shown so "who will get notified" has one answer. */
  workspace_admins: BrandAdmin[];
  /** Opted-in members of this brand specifically. */
  members: BrandMember[];
}

export async function brandNotificationRoster(brandId: string): Promise<BrandNotificationRoster> {
  const { data, error } = await supabase.rpc("brand_notification_roster", {
    p_brand_id: brandId,
  });
  if (error) throw asAppError(error);
  return (data ?? { brand_id: brandId, workspace_admins: [], members: [] }) as BrandNotificationRoster;
}

export async function addBrandMember(brandId: string, userId: string): Promise<void> {
  const { error } = await supabase.rpc("add_brand_member", {
    p_brand_id: brandId,
    p_user_id: userId,
  });
  if (error) throw asAppError(error);
}

export async function removeBrandMember(brandId: string, userId: string): Promise<void> {
  const { error } = await supabase.rpc("remove_brand_member", {
    p_brand_id: brandId,
    p_user_id: userId,
  });
  if (error) throw asAppError(error);
}

export async function setBrandNotifications(brandId: string, enabled: boolean): Promise<void> {
  const { error } = await supabase.rpc("set_brand_notifications", {
    p_brand_id: brandId,
    p_enabled: enabled,
  });
  if (error) throw asAppError(error);
}

// ---------------------------------------------------------------- the choice

// Which brand the person is looking at, remembered per workspace — switching
// business must not carry labtest1's filter into sabu's account list. Absent
// means "all brands", which is also what an unreadable localStorage falls back
// to: showing everything is the honest failure, showing nothing is not.
const KEY = (workspaceId: string) => `simino.brand.${workspaceId}`;

export function rememberedBrandId(workspaceId: string): string | null {
  try {
    return localStorage.getItem(KEY(workspaceId));
  } catch {
    return null;
  }
}

export function rememberBrand(workspaceId: string, brandId: string | null): void {
  try {
    if (brandId) localStorage.setItem(KEY(workspaceId), brandId);
    else localStorage.removeItem(KEY(workspaceId));
  } catch {
    // Not remembering is a small loss; failing here would not be.
  }
}
